import { useEffect, useState } from 'react';

// Measure an element's live block size. The height counterpart to `useMeasuredWidth` — for content
// whose height is NOT a fixed design constant (it varies by width/locale/variant), so a caller that
// needs to reserve exactly that much space (e.g. a top band another layer insets by) cannot hardcode it
// and must not fall out of sync as the content reflows.
//
// Returns the ref setter first so a call site reads `const [ref, height] = useMeasuredHeight()`. `height`
// is null until the first observation — i.e. on the server and on the very first client paint — so every
// consumer must state what it does with "not measured yet" rather than assume a number.
export function useMeasuredHeight(): [(el: HTMLElement | null) => void, number | null] {
  // The element lives in STATE, not a ref: a ref mutation doesn't re-run the effect, so the observer
  // would never attach on the commit that first renders the node.
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!el) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      // No observer (old Safari, jsdom): measure once so the value is at least right at mount.
      setHeight(el.getBoundingClientRect().height);
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      // The BORDER box, not `contentRect` (the content box): a caller reserving "exactly that much
      // space" needs the band's whole footprint, PADDING included — the personal-sale banner slot
      // carries a 24px top gap as padding, and measuring the content box left every layer that insets
      // by --personal-sale-banner-h (the fullscreen chat's top, the content region) 24px short, so the
      // chat covered the banner's bottom. `borderBoxSize.blockSize` is the height in a horizontal
      // writing mode; fall back to the bounding rect (also border-box) where it's unavailable — which
      // matches the no-ResizeObserver path below, so the two can no longer disagree.
      const next = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.target.getBoundingClientRect().height;
      if (typeof next === 'number') setHeight(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);

  return [setEl, height];
}
